import React from "react";
import { Tooltip } from "@mui/material";
import { InfoRounded } from "@mui/icons-material";
import { gapColourCheck, MAX_GAP_DAYS } from "../helpers/statusColor";
import { mobiliTheme } from "../themes/mobiliTheme";

export interface GapIndicatorProps {
  gapInDays: number;
  /** A justification exists for this gap. */
  hasJustification: boolean;
  /** Opens the justification dialog. Omit where the view cannot manage gaps. */
  onClick?: () => void;
}

/**
 * The rule between two appointments, annotated with the days between them.
 *
 * Replaces a 25px colour-filled Avatar plus an overlapping InfoRounded Badge,
 * which together stood ~45px tall to carry one line of 14px text. The three
 * things that had to survive are all still here, just not as boxes:
 *
 * - **severity** → the dot, still `gapColourCheck`, so the pink/red split
 *   between a justified and an unjustified over-long gap is unchanged;
 * - **justification present** → the inline info glyph, sized under the text so
 *   it cannot drive the row height;
 * - **click to view/edit** → the whole label, not just the former avatar.
 *
 * Height is the text's line box plus 2px above and below, and nothing in the
 * row is allowed to be taller than that text.
 */
export const GapIndicator: React.FC<GapIndicatorProps> = ({
  gapInDays,
  hasJustification,
  onClick,
}) => {
  const isOverLimit = gapInDays > MAX_GAP_DAYS;
  const tooltip = isOverLimit
    ? hasJustification
      ? `Lücke von ${gapInDays} Tagen – begründet`
      : `Lücke von ${gapInDays} Tagen – Begründung erforderlich`
    : `Lücke von ${gapInDays} Tagen`;

  return (
    <div className="flex items-center gap-1.5 py-[2px]">
      <span className="h-px flex-1 bg-ink-line" />
      <Tooltip title={tooltip}>
        <span
          role={onClick ? "button" : undefined}
          tabIndex={onClick ? 0 : undefined}
          onClick={onClick}
          onKeyDown={
            onClick
              ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onClick();
                  }
                }
              : undefined
          }
          className={[
            "inline-flex shrink-0 items-center gap-1 text-dense tabular-nums text-ink-muted",
            isOverLimit ? "font-semibold" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ cursor: onClick ? "pointer" : "default" }}
        >
          <span
            aria-hidden
            className="h-2 w-2 shrink-0 rounded-pill"
            style={{ backgroundColor: gapColourCheck(gapInDays, hasJustification) }}
          />
          {gapInDays} Tage
          {hasJustification && (
            <InfoRounded sx={{ fontSize: 12, color: mobiliTheme.palette.info.dark }} />
          )}
        </span>
      </Tooltip>
      <span className="h-px flex-1 bg-ink-line" />
    </div>
  );
};

export default GapIndicator;
